import React from "react";
import BarChart from "./BarChart";
import ColumnChart from "./ColumnChart";
import LineChart from "./LineChart";
import LegacyChart from "interface/LegacyChart";
import ComboChart from "./ComboChart";
import styles from "styles/components/ZeroneChart.module.sass"

interface ZeroneChartProps {
    chart: LegacyChart;
}

const supportedChartTypes = ['line', 'bar', 'column', 'combo'];

/**
 * ZeroneChart component renders different types of charts based on the provided chart data.
 * It supports line, bar, column, and combo charts, and displays an annotation if available.
 *
 * @param {ZeroneChartProps} props - The properties for the ZeroneChart component.
 * 
 * @returns {JSX.Element} The rendered ZeroneChart component.
 */
const ZeroneChart: React.FC<ZeroneChartProps> = ({ chart }) => {
    return (
        <div className={styles['zerone-chart']}>
            <div className={styles['chart-header']}>
                <h2 className={styles['chart-title']}>{chart.title}</h2>
                {chart.annotation && (chart.annotation.title || chart.annotation?.value) && (
                    <div className={styles['chart-annotation']}>
                        <span className={styles['chart-annotation-title']}>{chart.annotation.title}</span>
                        {typeof chart.annotation.value === 'string' ? (
                            <p className={styles['chart-annotation-value']}>{chart.annotation.value}</p>
                        ) : Array.isArray(chart.annotation.value) ? (
                            <ul className={styles['chart-annotation-value-list']}>
                                {chart.annotation.value.map((value, index) => (
                                    <li key={index} className={styles['chart-annotation-value-item']}>{value}</li>
                                ))}
                            </ul>
                        ) : (
                            <p className={styles['chart-annotation-value']}>{chart.annotation.value}</p>
                        )}
                    </div>
                )}
            </div>
            {chart.type === 'line' && <LineChart chart={chart} />}
            {chart.type === 'bar' && <BarChart chart={chart} />}
            {chart.type === 'column' && <ColumnChart chart={chart} />}
            {chart.type === 'combo' && <ComboChart chart={chart} />}
            {!supportedChartTypes.includes(chart.type) && (
                <div className={styles['unsupported-chart']}>
                    <p>This chart is not available at the moment.</p>
                </div>
            )}
        </div>
    );
};

export default ZeroneChart;
